import { __awaiter } from "tslib";
import { fetchAIHubEnabled, isCreateModuleEnabled, isCreateNativeEnabled, isPrepareModuleEnabled, isPrepareNativeModuleEnabled, } from '@office-start/ai-hub-config-service';
import { fetchCapabilities } from '@office-start/capabilities-service';
import { fetchBCAIS, fetchIsCopilotRoute, fetchPinningEligible, isAdminDisabledBizChat, } from '@office-start/copilot-route-service';
import { fetchFeatureGates, getFeatureGates, } from '@office-start/feature-gates-service';
import { fetchFeatureSettings, getFeatureSettings, } from '@office-start/feature-settings-service';
import { fetchIdentity, getOfficeIdentity, } from '@office-start/identity-service';
import { fetchLocStrings } from '@office-start/loc-service';
import { getHarmonyAppDefinition } from '@office-start/mos-app-utilities';
import { fetchUserEntitlements, getUserEntitlements, } from '@office-start/user-entitlements-service';
import { getCandidateMOS3AppEntries, IMMERSIVEBIZCHAT_APP_ID, ONEDRIVE_APP_ID, PreinstalledAppNames, } from '../constants/preinstalledApps';
export const fetchPreinstalledApps = () => __awaiter(void 0, void 0, void 0, function* () {
    const capabilities = yield fetchCapabilities();
    const featureGatesResult = getFeatureGates();
    const { result: featureGates } = (featureGatesResult === null || featureGatesResult === void 0 ? void 0 : featureGatesResult.result)
        ? featureGatesResult
        : yield fetchFeatureGates();
    const featureSettingsResult = getFeatureSettings();
    const { result: featureSettings } = (featureSettingsResult === null || featureSettingsResult === void 0 ? void 0 : featureSettingsResult.result)
        ? featureSettingsResult
        : yield fetchFeatureSettings();
    const userEntitlementsResult = getUserEntitlements();
    const { result: userEntitlements } = (userEntitlementsResult === null || userEntitlementsResult === void 0 ? void 0 : userEntitlementsResult.result)
        ? userEntitlementsResult
        : yield fetchUserEntitlements();
    if (!capabilities) {
        throw new Error('Capabilities not found');
    }
    //TODO : Workaround since general localization solution for preinstalled app is not ready yet. It should be replaced later.
    const summarizeActionText = yield fetchSummarizeActionText();
    const enableAIHub = yield fetchAIHubEnabled();
    const identityResult = getOfficeIdentity();
    const identity = identityResult ? identityResult : yield fetchIdentity();
    const isCopilotRoute = yield fetchIsCopilotRoute();
    const isBCAIS = yield fetchBCAIS();
    const isPinningEligible = yield fetchPinningEligible();
    return [
        ...(yield getPreinstalledApps(capabilities, featureGates, featureSettings, userEntitlements || undefined, summarizeActionText, enableAIHub, identity, isCopilotRoute, isBCAIS, isPinningEligible)),
    ];
});
const getSummarizeActionText = (locStrings) => { var _a, _b; return (_b = (_a = locStrings === null || locStrings === void 0 ? void 0 : locStrings.metaOSHubResources) === null || _a === void 0 ? void 0 : _a.summarizeActionText) !== null && _b !== void 0 ? _b : ''; };
export const fetchSummarizeActionText = () => __awaiter(void 0, void 0, void 0, function* () {
    const locStrings = yield fetchLocStrings();
    return getSummarizeActionText(locStrings);
});
// eslint-disable-next-line max-params
export function getFirstPartyAppDefinition(appId, locStrings, isBCAISContext, isEligibleForAnyCopilot) {
    return __awaiter(this, void 0, void 0, function* () {
        const summarizeActionText = locStrings
            ? getSummarizeActionText(locStrings)
            : yield fetchSummarizeActionText();
        const candidateMOS3AppEntries = yield getCandidateMOS3AppEntries(summarizeActionText, isBCAISContext, isEligibleForAnyCopilot);
        const appEntry = candidateMOS3AppEntries.find((entry) => entry.appId === appId);
        const harmonyApp = getHarmonyAppDefinition(appEntry === null || appEntry === void 0 ? void 0 : appEntry.definition);
        return harmonyApp;
    });
}
export function isFirstPartyApp(appId) {
    return appId === ONEDRIVE_APP_ID || appId === IMMERSIVEBIZCHAT_APP_ID;
}
const fetchWithCatch = (fetchFn) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    try {
        return (_a = (yield fetchFn())) === null || _a === void 0 ? void 0 : _a.result;
    }
    catch (_b) {
        return undefined;
    }
});
export const isCopilotEnabled = () => __awaiter(void 0, void 0, void 0, function* () {
    const [featureGates, featureSettings, userEntitlements, enableAIHub, identity, isCopilotRoute, isBCAISUser, isPinningEligible,] = yield Promise.all([
        fetchWithCatch(fetchFeatureGates),
        fetchWithCatch(fetchFeatureSettings),
        fetchWithCatch(fetchUserEntitlements),
        fetchAIHubEnabled(),
        fetchIdentity(),
        fetchIsCopilotRoute(),
        fetchBCAIS(),
        fetchPinningEligible(),
    ]);
    return isBizChatEnabled(featureGates, featureSettings, userEntitlements, enableAIHub, identity, isCopilotRoute, isBCAISUser, isPinningEligible);
});
//Logic to determine if user has access to the copilot business chat app
export function isBizChatEnabled(featureGates, featureSettings, userEntitlements, enableAIHub, identity, isCopilotRoute, isBCAISUser, isPinningEligible) {
    var _a, _b, _c, _d;
    // early return for MSA users
    if (identity.isMsa) {
        return false;
    }
    // early return if force-install FG is enabled
    if (enableAIHub ||
        ((_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.immersiveBizchatM365AppGates) === null || _a === void 0 ? void 0 : _a.immersiveBizchatM365AppEnabled)) {
        return true;
    }
    const copilotMode = (_b = featureSettings === null || featureSettings === void 0 ? void 0 : featureSettings.copilot) === null || _b === void 0 ? void 0 : _b.mode;
    const isStandaloneApp = (_c = userEntitlements === null || userEntitlements === void 0 ? void 0 : userEntitlements.isStandaloneCopilotApp) !== null && _c !== void 0 ? _c : false;
    // Standalone app always will have copilot experience in ModeB
    if (isStandaloneApp && copilotMode === 'ModeB') {
        return true;
    }
    // Rule0
    if (isBCAISUser) {
        return true;
    }
    if (isAdminDisabledBizChat(featureGates, userEntitlements) &&
        ((_d = featureGates === null || featureGates === void 0 ? void 0 : featureGates.immersiveBizchatM365AppGates) === null || _d === void 0 ? void 0 : _d.immersiveBizchatM365AppBlockLaunchAfterAdminOverrideSettingEnabled)) {
        return false;
    }
    // R1
    if (!copilotMode) {
        return false;
    }
    // R2
    if (isCopilotRoute) {
        return true;
    }
    // R3
    if (copilotMode === 'ModeA') {
        return false;
    }
    // R4
    // isPinningEligible
    if (isPinningEligible) {
        return true;
    }
    // R5
    return false;
}
const shouldInstall = (capabilitiesConfig, appEnumName, featureGates, featureSettings, userEntitlements, enableAIHub, identity, isCopilotRoute, isBCAIS, isPinningEligible) => {
    var _a, _b, _c, _d;
    switch (appEnumName) {
        case PreinstalledAppNames.SpfxTestApp:
        case PreinstalledAppNames.Test:
            return capabilitiesConfig.m365TestAppEnabled;
        case PreinstalledAppNames.Todo:
            return capabilitiesConfig.toDoM365AppEnabled;
        case PreinstalledAppNames.OneDriveMsa:
            return ((_b = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.oneDriveConsumerMosAppGates) === null || _a === void 0 ? void 0 : _a.oneDriveConsumerMosAppEnabled) !== null && _b !== void 0 ? _b : false);
        case PreinstalledAppNames.Store:
            return capabilitiesConfig.storeAppEnabled;
        case PreinstalledAppNames.Yammer:
            return capabilitiesConfig.yammerM365AppEnabled;
        case PreinstalledAppNames.ImmersiveBizchat:
            return isBizChatEnabled(featureGates, featureSettings, userEntitlements, enableAIHub, identity, isCopilotRoute, isBCAIS, isPinningEligible);
        case PreinstalledAppNames.OneDrive:
            return ((_d = (_c = featureGates === null || featureGates === void 0 ? void 0 : featureGates.oneDriveMosAppGates) === null || _c === void 0 ? void 0 : _c.oneDriveMosAppEnabled) !== null && _d !== void 0 ? _d : false);
        case PreinstalledAppNames.Prepare:
            return (enableAIHub &&
                isPrepareModuleEnabled(featureGates) &&
                !isPrepareNativeModuleEnabled(featureGates));
        case PreinstalledAppNames.Create:
            return (enableAIHub &&
                isCreateModuleEnabled(featureGates) &&
                !isCreateNativeEnabled(featureGates));
        default:
            return false;
    }
};
const getPreinstalledApps = (capabilitiesConfig, featureGates, featureSettings, userEntitlements, summarizeActionText, enableAIHub, identity, isCopilotRoute, isBCAIS, isPinningEligible) => __awaiter(void 0, void 0, void 0, function* () {
    const appDefinitions = [];
    const candidateMOS3AppEntries = yield getCandidateMOS3AppEntries(summarizeActionText);
    candidateMOS3AppEntries.forEach((appEntry) => {
        if (shouldInstall(capabilitiesConfig, appEntry.appEnumName, featureGates, featureSettings, userEntitlements, enableAIHub, identity, isCopilotRoute, isBCAIS, isPinningEligible)) {
            const harmonyApp = getHarmonyAppDefinition(appEntry.definition);
            harmonyApp && appDefinitions.push(harmonyApp);
        }
    });
    return appDefinitions;
});
//# sourceMappingURL=fetchPreinstalledApps.js.map